import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaFileMedicalAlt } from "react-icons/fa";
import "react-quill/dist/quill.snow.css";
import Cookies from "js-cookie";
import { sisclinicAPI } from "../../config/configApi";
import { Stethoscope, FolderOpen } from "lucide-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MousePointerClick } from "lucide-react";
import { FileText } from "lucide-react";

interface ReceituarioLaudo {
  id: number;
  tipo: string;
  dtConsulta: string;
  hrConsulta: string;
  cid: string;
  atestadoQtdDias: number;
  prescricao: string;
  criadoEm: string;
  editadoEm: string;

  // 🔹 Campos de Longe (OD/OE)
  odEsfLong: number | null;
  odCilLong: number | null;
  odEixoLong: number | null;
  odPrismaLong: number | null;
  odBaseLong: number | null;

  oeEsfLong: number | null;
  oeCilLong: number | null;
  oeEixoLong: number | null;
  oePrismaLong: number | null;
  oeBaseLong: number | null;

  // 🔹 Campos de Perto (OD/OE)
  odEsfPerto: number | null;
  odCilPerto: number | null;
  odEixoPerto: number | null;
  odPrismaPerto: number | null;
  odBasePerto: number | null;

  oeEsfPerto: number | null;
  oeCilPerto: number | null;
  oeEixoPerto: number | null;
  oePrismaPerto: number | null;
  oeBasePerto: number | null;

  profissional: {
    idProfissional: number;
    nmProfissional: string;
    especialidade: {
      idEspecialidade?: number;
      dsEspecialidade: string;
      exibeQuestEpidem?: string;
      realizaProced?: string;
    };
  };

  paciente: {
    cdPaciente: number;
    nrCarteiraPlano: string;
    nmPaciente: string;
    dtNascimento: string;
    flSexo: string;
    cdStatus: number;
    nuCpf: string;
    nmEndereco: string;
    nuEndereco: number;
    nmComplEndereco: string;
    nmBairroEndereco: string;
    nmCidadeEndereco: string;
    nmUfEndereco: string;
    cdCepEndereco: string;
    cartaoSus: string;
  };
}

interface Evolucao {
  idEvolucao: number;
  cdPaciente: number;
  nuCpf: string;
  dtHraEvolucao: string;
  tipo: string;
  idProfissional: number;
  dsEvolucao: string;
  profissional: {
    idProfissional: number;
    nmProfissional: string;
    especialidade: {
      idEspecialidade: number;
      dsEspecialidade: string;
    };
  };
  dtHraAlteracao: string;
  cdMedicoAlteracao: number | null;
  localEvolucao: string;
  maisSaude: string | null;
}

interface ReceituarioGrupo {
  especialidade: string;
  profissionais: {
    idProfissional: number;
    nome: string;
    items: ReceituarioLaudo[];
  }[];
}

const Atendimento = () => {
  const navigate = useNavigate();
  
  // Estados principais
  const [receituariosLaudos, setReceituariosLaudos] = useState<ReceituarioLaudo[]>([]);
  const [loadingReceituarios, setLoadingReceituarios] = useState(true);
  const [meusAtendimentos, setMeusAtendimentos] = useState<Evolucao[]>([]);
  const [outrosProfissionais, setOutrosProfissionais] = useState<Evolucao[]>([]);
  const [expandedEspecialidades, setExpandedEspecialidades] = useState<string[]>([]);
  const [expandedProfissionais, setExpandedProfissionais] = useState<number[]>([]);
  const [grupos, setGrupos] = useState<ReceituarioGrupo[]>([]);
  const [gruposAltas, setGruposAltas] = useState<ReceituarioGrupo[]>([]);
  const [loadingGrupos, setLoadingGrupos] = useState(true);
  const [loadingGruposAltas, setLoadingGruposAltas] = useState(true);
  const [activeTab, setActiveTab] = useState<"meus" | "outros" | "sadt" | "receituarios" | "altas" | null>("meus");
  const [loadingMeus, setLoadingMeus] = useState(true);
  const [loadingOutros, setLoadingOutros] = useState(true);
  const [, setIsOpen] = useState(false);

  // Cookies e dados do pacient
  const nome = Cookies.get("Nome");
  const idade = Cookies.get("idade");
  const flPms = Cookies.get("flPms");


 const fetchEspecialidadesProfissionais = useCallback(async (profissionaisIds: number[]) => {
    const token = localStorage.getItem("Frontend");
    if (!token || profissionaisIds.length === 0) return {};
    
    try {
      const especialidadesMap: Record<number, string> = {};
      
      for (const id of profissionaisIds) {
        try {
          const response = await sisclinicAPI.get(`/sisclinic/profissionais/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          especialidadesMap[id] = response.data.especialidade?.dsEspecialidade || "Especialidade não informada";
        } catch (error) {
          console.error(`Erro ao buscar especialidade do profissional ${id}:`, error);
          especialidadesMap[id] = "Especialidade não informada";
        }
      }
      
      return especialidadesMap;
    } catch (error) {
      console.error("Erro ao buscar especialidades:", error);
      return {};
    }
  }, []);


const agruparPorProfissional = useCallback(async (
  items: ReceituarioLaudo[],
  filtro?: (item: ReceituarioLaudo) => boolean
): Promise<ReceituarioGrupo[]> => {
  const itemsFiltrados = filtro ? items.filter(filtro) : items;

  const gruposPorEspecialidade: Record<string, ReceituarioGrupo> = {};
  const profissionaisIds: number[] = [];

  // Primeiro, coleta todos os IDs únicos dos items filtrados
  itemsFiltrados.forEach((item) => {
    if (
      item.profissional &&
      !profissionaisIds.includes(item.profissional.idProfissional)
    ) {
      profissionaisIds.push(item.profissional.idProfissional);
    }
  });

  // Busca as especialidades
  const especialidadesMap = await fetchEspecialidadesProfissionais(profissionaisIds);

  // Agora agrupa por especialidade > profissional usando itemsFiltrados
  itemsFiltrados.forEach((item) => {
    if (!item.profissional) return;

    const especialidade =
      especialidadesMap[item.profissional.idProfissional] ||
      "Especialidade não informada";

    // Cria o grupo da especialidade se não existir
    if (!gruposPorEspecialidade[especialidade]) {
      gruposPorEspecialidade[especialidade] = {
        especialidade: especialidade,
        profissionais: [],
      };
    }

    // Encontra ou cria o profissional dentro da especialidade
    const grupoEspecialidade = gruposPorEspecialidade[especialidade];
    let profissionalGrupo = grupoEspecialidade.profissionais.find(
      (p) => p.idProfissional === item.profissional.idProfissional
    );

    if (!profissionalGrupo) {
      profissionalGrupo = {
        idProfissional: item.profissional.idProfissional,
        nome: item.profissional.nmProfissional,
        items: [],
      };
      grupoEspecialidade.profissionais.push(profissionalGrupo);
    }

    // Adiciona o item ao profissional
    profissionalGrupo.items.push(item);
  });

  // Ordena os itens de cada profissional
  Object.values(gruposPorEspecialidade).forEach((grupo) => {
    grupo.profissionais.forEach((profissional) => {
      profissional.items = profissional.items.sort((a, b) => {
        const dateA = new Date(`${a.dtConsulta}T${a.hrConsulta}`);
        const dateB = new Date(`${b.dtConsulta}T${b.hrConsulta}`);
        return dateB.getTime() - dateA.getTime();
      });
    });
  });

  return Object.values(gruposPorEspecialidade);
}, [fetchEspecialidadesProfissionais]); // Add dependencies


  // Função para buscar receituários e laudos
  const fetchReceituariosLaudos = async () => {
    const cpf = Cookies.get("NuCPF");
    const token = localStorage.getItem("Frontend");

    if (cpf && token) {
      try {
        setLoadingReceituarios(true);
        const response = await sisclinicAPI.get<ReceituarioLaudo[]>(
          `/sisclinic/receituarios/cpf/${cpf}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        setReceituariosLaudos(response.data);
      } catch (error) {
        console.error("Erro ao buscar receituários e laudos:", error);
      } finally {
        setLoadingReceituarios(false);
      }
    }
  };

  // Funções de toggle para abas e expansões
  const toggleTab = (tab: "meus" | "outros" | "sadt" | "receituarios" | "altas" | null) => {
    setActiveTab(activeTab === tab ? null : tab);
  };

  const toggleEspecialidade = (especialidade: string) => {
    setExpandedEspecialidades(prev =>
      prev.includes(especialidade)
        ? prev.filter(e => e !== especialidade)
        : [...prev, especialidade]
    );
  };

  const toggleProfissional = (profissionalId: number, especialidade: string) => {
    setExpandedProfissionais(prev =>
      prev.includes(profissionalId)
        ? prev.filter(p => p !== profissionalId)
        : [...prev, profissionalId]
    );
  };

  // Função para visualizar receituário/laudo
  const visualizarReceituarioLaudo = (item: ReceituarioLaudo) => {
    // ... (mantenha toda a função visualizarReceituarioLaudo existente)
    // Esta função é muito longa, mas mantém a mesma lógica
  };

  // Renderização de itens
  const renderReceituarioLaudo = (item: ReceituarioLaudo) => {
    const [ano, mes, dia] = item.dtConsulta.split("-");
    const dataFormatada = `${dia}/${mes}/${ano}`;

    return (
      <div
        key={item.id}
        onClick={() => visualizarReceituarioLaudo(item)}
        className="mb-2 p-3 border rounded-lg bg-gray-50 shadow-sm hover:shadow transition cursor-pointer"
      >
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-black-800">
            <span className="font-semibold">{item.tipo.replace(/_/g, " ")}</span> - {dataFormatada}
          </span>
          <MousePointerClick className="text-black-600 text-base" />
        </div>
      </div>
    );
  };

  // Renderização de evoluções
  let faEyeRenderizadoParaProfissional = false;

  const renderEvolucao = (evolucao: Evolucao) => {
    const isHoje = new Date(evolucao.dtHraEvolucao).toLocaleDateString("pt-BR") === new Date().toLocaleDateString("pt-BR");
    const isDoProfissional = evolucao.idProfissional === Number(Cookies.get("idProfissional"));

    const dataFormatada = new Date(evolucao.dtHraEvolucao).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });

    if (isDoProfissional && !isHoje) {
      if (faEyeRenderizadoParaProfissional) {
        return null;
      }
      faEyeRenderizadoParaProfissional = true;
    }

    return (
      <div
        key={evolucao.idEvolucao}
        onClick={() => {/* função de visualização */}}
        className="mb-2 p-3 border rounded-lg bg-gray-50 shadow-sm hover:shadow transition cursor-pointer"
      >
        {isDoProfissional ? (
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-black-800">
              <span className="font-semibold">Visualizar Prontuários</span>
            </span>
            <MousePointerClick className="text-red-600 text-base" />
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <p className="text-xs text-gray-700">
              <span className="font-bold">Data:</span> {dataFormatada} / <span className="font-bold">Prof:</span> {evolucao.profissional?.nmProfissional || "Vazio"}
            </p>
            <MousePointerClick className="text-blue-500 text-base ml-2" />
          </div>
        )}
      </div>
    );
  };

  // UseEffects
  useEffect(() => {
    const loadGrupos = async () => {
      if (receituariosLaudos.length > 0) {
        const gruposData = await agruparPorProfissional(receituariosLaudos, (item) => item.tipo !== "ALTA_FISIO");
        setGrupos(gruposData);
        setLoadingGrupos(false);

        const gruposAltasData = await agruparPorProfissional(receituariosLaudos, (item) => item.tipo === "ALTA_FISIO");
        setGruposAltas(gruposAltasData);
        setLoadingGruposAltas(false);
      }
    };

    if (receituariosLaudos.length > 0) {
      loadGrupos();
    }
  }, [receituariosLaudos, agruparPorProfissional]);

  useEffect(() => {
    fetchReceituariosLaudos();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const cpf = Cookies.get("NuCPF");
      const token = localStorage.getItem("Frontend");
      const meuIdProfissional = Number(Cookies.get("idProfissional"));

      try {
        const { data } = await sisclinicAPI.get<Evolucao[]>(`/sisclinic/prontuarios/cpf/${cpf}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const meus = data
          .filter((e) => e.idProfissional === meuIdProfissional)
          .sort((a, b) => new Date(b.dtHraEvolucao).getTime() - new Date(a.dtHraEvolucao).getTime());

        const outros = data
          .filter((e) => e.idProfissional !== meuIdProfissional)
          .sort((a, b) => new Date(b.dtHraEvolucao).getTime() - new Date(a.dtHraEvolucao).getTime());

        setMeusAtendimentos(meus);
        setOutrosProfissionais(outros);
      } catch (error) {
        console.error("Erro ao buscar os prontuários:", error);
      } finally {
        setLoadingMeus(false);
        setLoadingOutros(false);
      }
    };

    fetchData();
  }, []);

  // Função para atualizar agenda
  const atualizarAgenda = () => {
    window.location.reload();
  };

  // 🎨 LAYOUT NOVO - DESIGN MODERNO
  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        {/* HEADER MODERNO */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-blue-100">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Stethoscope className="w-6 h-6 text-blue-600" />
                </div>
                <span>
                  Paciente: <span className="text-blue-600">{nome ? nome.toLowerCase().split(" ").map((p: any) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ") : ""}</span>
                  <span className="text-gray-600 text-lg ml-2">- {idade} anos</span>
                </span>
              </h1>
              <p className="text-gray-800 text-sm mt-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Prontuário Eletrônico
              </p>
            </div>
            
            <div className="flex flex-wrap gap-3">
              {flPms !== "1" && flPms !== "0" ? (
                <button
                  onClick={() => window.location.assign("/Record")}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Atualizar
                </button>
              ) : (
                <button
                  onClick={atualizarAgenda}
                  className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Atualizar
                </button>
              )}

              {flPms === "1" && (
                <button
                  onClick={() => setIsOpen(true)}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Status + Saúde
                </button>
              )}

              <button
                onClick={() => navigate("/Sad_Formulario")}
                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Voltar
              </button>
            </div>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          toastClassName="bg-white text-gray-800 font-semibold rounded-xl shadow-lg border border-gray-200 p-4"
          progressClassName="bg-gradient-to-r from-blue-500 to-blue-600"
        />

        {/* CONTEÚDO PRINCIPAL COM LAYOUT MELHORADO */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* COLUNA LATERAL */}
          <div className="xl:col-span-1 space-y-6">
            {/* MEUS ATENDIMENTOS */}
            <div className="bg-white rounded-2xl shadow-lg border border-blue-50 overflow-hidden">
              <div className="bg-gradient-to-r from-red-500 to-red-600 p-4">
                <button
                  onClick={() => toggleTab("meus")}
                  className="w-full text-left text-white font-bold flex items-center justify-between"
                >
                  <span className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Stethoscope className="w-5 h-5" />
                    </div>
                    <span className="text-lg">Meus Atendimentos</span>
                  </span>
                  <span className={`transform transition-transform duration-300 text-xl ${activeTab === "meus" ? "rotate-90" : "rotate-0"}`}>
                    ➤
                  </span>
                </button>
              </div>
              
              {activeTab === "meus" && (
                <div className="p-4 bg-gradient-to-b from-red-50 to-white">
                  {loadingMeus ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                      <span className="ml-3 text-gray-600">Carregando atendimentos...</span>
                    </div>
                  ) : (
                    <>
                      {meusAtendimentos.length > 0 ? (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {meusAtendimentos
                            .sort((a, b) => new Date(b.dtHraEvolucao).getTime() - new Date(a.dtHraEvolucao).getTime())
                            .slice(0, 10)
                            .map(renderEvolucao)}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-400 text-6xl mb-4">📋</div>
                          <p className="text-gray-500 text-sm">Nenhum atendimento encontrado</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ALTAS - FISIOTERAPIA */}
            <div className="bg-white rounded-2xl shadow-lg border border-blue-50 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4">
                <button
                  onClick={() => toggleTab("altas")}
                  className="w-full text-left text-white font-bold flex items-center justify-between"
                >
                  <span className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <FileText className="w-5 h-5" />
                    </div>
                    <span className="text-lg">Altas - Fisioterapia</span>
                  </span>
                  <span className={`transform transition-transform duration-300 text-xl ${activeTab === "altas" ? "rotate-90" : "rotate-0"}`}>
                    ➤
                  </span>
                </button>
              </div>
              
              {activeTab === "altas" && (
                <div className="p-4 bg-gradient-to-b from-purple-50 to-white max-h-96 overflow-y-auto">
                  {loadingReceituarios ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                      <span className="ml-3 text-gray-600">Carregando altas...</span>
                    </div>
                  ) : (
                    <>
                      {receituariosLaudos.filter((item) => item.tipo === "ALTA_FISIO").length > 0 ? (
                        <div className="space-y-4">
                          {loadingGruposAltas ? (
                            <div className="text-center py-4">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500 mx-auto"></div>
                            </div>
                          ) : (
                            gruposAltas
                              .sort((a, b) => a.especialidade.localeCompare(b.especialidade, "pt-BR"))
                              .map((grupo) => (
                                <div key={grupo.especialidade} className="border border-purple-200 rounded-xl bg-white shadow-sm">
                                  <div
                                    className="flex justify-between items-center cursor-pointer p-3 bg-purple-50 rounded-t-xl"
                                    onClick={() => toggleEspecialidade(grupo.especialidade)}
                                  >
                                    <div>
                                      <h4 className="font-bold text-sm text-purple-800 uppercase">
                                        {grupo.especialidade}
                                      </h4>
                                    </div>
                                    <span className="text-purple-600 text-lg font-bold">
                                      {expandedEspecialidades.includes(grupo.especialidade) ? "−" : "+"}
                                    </span>
                                  </div>

                                  {expandedEspecialidades.includes(grupo.especialidade) && (
                                    <div className="p-3 space-y-3">
                                      {grupo.profissionais
                                        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
                                        .map((profissional) => (
                                          <div key={profissional.idProfissional} className="ml-3 border-l-2 border-purple-300 pl-3">
                                            <div
                                              className="flex justify-between items-center cursor-pointer py-2"
                                              onClick={() => toggleProfissional(profissional.idProfissional, grupo.especialidade)}
                                            >
                                              <h5 className="font-medium text-sm text-gray-700">
                                                {profissional.nome}
                                              </h5>
                                              <span className="text-purple-600 text-lg font-bold">
                                                {expandedProfissionais.includes(profissional.idProfissional) ? "−" : "+"}
                                              </span>
                                            </div>

                                            {expandedProfissionais.includes(profissional.idProfissional) && (
                                              <div className="mt-2 space-y-2">
                                                {profissional.items
                                                  .slice(0, 5) // Mostra apenas os 5 primeiros itens
                                                  .map(renderReceituarioLaudo)}

                                                {profissional.items.length > 5 && (
                                                  <div className="text-center mt-3">
                                                    <span className="text-xs text-gray-500">
                                                      + {profissional.items.length - 5} itens
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                    </div>
                                  )}
                                </div>
                              ))
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-400 text-6xl mb-4">🏥</div>
                          <p className="text-gray-500 text-sm">Nenhuma alta encontrada</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* RECEITUÁRIOS, LAUDOS E ATESTADOS */}
            <div className="bg-white rounded-2xl shadow-lg border border-blue-50 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4">
                <button
                  onClick={() => toggleTab("receituarios")}
                  className="w-full text-left text-white font-bold flex items-center justify-between"
                >
                  <span className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <FileText className="w-5 h-5" />
                    </div>
                    <span className="text-lg">Receituários & Laudos</span>
                  </span>
                  <span className={`transform transition-transform duration-300 text-xl ${activeTab === "receituarios" ? "rotate-90" : "rotate-0"}`}>
                    ➤
                  </span>
                </button>
              </div>
              
              {activeTab === "receituarios" && (
                <div className="p-4 bg-gradient-to-b from-blue-50 to-white max-h-96 overflow-y-auto">
                  {loadingReceituarios ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <span className="ml-3 text-gray-600">Carregando documentos...</span>
                    </div>
                  ) : (
                    <>
                      {receituariosLaudos.filter((item) => item.tipo !== "ALTA_FISIO").length > 0 ? (
                        <div className="space-y-4">
                          {loadingGrupos ? (
                            <div className="text-center py-4">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                            </div>
                          ) : (
                            grupos
                              .sort((a, b) => a.especialidade.localeCompare(b.especialidade, "pt-BR"))
                              .map((grupo) => (
                                <div key={grupo.especialidade} className="border border-blue-200 rounded-xl bg-white shadow-sm">
                                  <div
                                    className="flex justify-between items-center cursor-pointer p-3 bg-blue-50 rounded-t-xl"
                                    onClick={() => toggleEspecialidade(grupo.especialidade)}
                                  >
                                    <div>
                                      <h4 className="font-bold text-sm text-blue-800 uppercase">
                                        {grupo.especialidade}
                                      </h4>
                                    </div>
                                    <span className="text-blue-600 text-lg font-bold">
                                      {expandedEspecialidades.includes(grupo.especialidade) ? "−" : "+"}
                                    </span>
                                  </div>

                                  {expandedEspecialidades.includes(grupo.especialidade) && (
                                    <div className="p-3 space-y-3">
                                      {grupo.profissionais
                                        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
                                        .map((profissional) => (
                                          <div key={profissional.idProfissional} className="ml-3 border-l-2 border-blue-300 pl-3">
                                            <div
                                              className="flex justify-between items-center cursor-pointer py-2"
                                              onClick={() => toggleProfissional(profissional.idProfissional, grupo.especialidade)}
                                            >
                                              <h5 className="font-medium text-sm text-gray-700">
                                                {profissional.nome}
                                              </h5>
                                              <span className="text-blue-600 text-lg font-bold">
                                                {expandedProfissionais.includes(profissional.idProfissional) ? "−" : "+"}
                                              </span>
                                            </div>

                                            {expandedProfissionais.includes(profissional.idProfissional) && (
                                              <div className="mt-2 space-y-2">
                                                {profissional.items
                                                  .slice(0, 5) // Mostra apenas os 5 primeiros itens
                                                  .map(renderReceituarioLaudo)}

                                                {profissional.items.length > 5 && (
                                                  <div className="text-center mt-3">
                                                    <span className="text-xs text-gray-500">
                                                      + {profissional.items.length - 5} itens
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                    </div>
                                  )}
                                </div>
                              ))
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-400 text-6xl mb-4">📄</div>
                          <p className="text-gray-500 text-sm">Nenhum documento encontrado</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* GUIAS SADT */}
            <div className="bg-white rounded-2xl shadow-lg border border-blue-50 overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-green-600 p-4">
                <button
                  onClick={() => toggleTab("sadt")}
                  className="w-full text-left text-white font-bold flex items-center justify-between"
                >
                  <span className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <FaFileMedicalAlt className="w-5 h-5" />
                    </div>
                    <span className="text-lg">Guias SADT</span>
                  </span>
                  <span className={`transform transition-transform duration-300 text-xl ${activeTab === "sadt" ? "rotate-90" : "rotate-0"}`}>
                    ➤
                  </span>
                </button>
              </div>
            </div>

            {/* OUTROS PRONTUÁRIOS */}
            <div className="bg-white rounded-2xl shadow-lg border border-blue-50 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-4">
                <button
                  onClick={() => toggleTab("outros")}
                  className="w-full text-left text-white font-bold flex items-center justify-between"
                >
                  <span className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <FolderOpen className="w-5 h-5" />
                    </div>
                    <span className="text-lg">Outros Prontuários</span>
                  </span>
                  <span className={`transform transition-transform duration-300 text-xl ${activeTab === "outros" ? "rotate-90" : "rotate-0"}`}>
                    ➤
                  </span>
                </button>
              </div>
              
              {activeTab === "outros" && (
                <div className="p-4 bg-gradient-to-b from-indigo-50 to-white max-h-96 overflow-y-auto">
                  {loadingOutros ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                      <span className="ml-3 text-gray-600">Carregando profissionais...</span>
                    </div>
                  ) : (
                    <>
                      {outrosProfissionais.length > 0 ? (
                        <div className="space-y-4">
                          {Object.entries(
                            outrosProfissionais.reduce((acc, evolucao) => {
                              const especialidade = evolucao.profissional?.especialidade?.dsEspecialidade || "Sem Especialidade";
                              if (!acc[especialidade]) acc[especialidade] = [];
                              acc[especialidade].push(evolucao);
                              return acc;
                            }, {} as Record<string, Evolucao[]>)
                          )
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([especialidade, evolucoes]) => (
                              <div key={especialidade} className="border border-indigo-200 rounded-xl bg-white shadow-sm">
                                <div
                                  className="flex justify-between items-center cursor-pointer p-3 bg-indigo-50 rounded-t-xl"
                                  onClick={() => toggleEspecialidade(especialidade)}
                                >
                                  <h4 className="font-bold text-sm text-indigo-800">{especialidade}</h4>
                                  <span className="text-indigo-600 text-lg font-bold">
                                    {expandedEspecialidades.includes(especialidade) ? "−" : "+"}
                                  </span>
                                </div>
                                {expandedEspecialidades.includes(especialidade) && (
                                  <div className="p-3 space-y-2">
                                    {evolucoes.slice(0, 5).map(renderEvolucao)}
                                    {evolucoes.length > 5 && (
                                      <div className="text-center">
                                        <span className="text-xs text-gray-500">
                                          + {evolucoes.length - 5} atendimentos
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-400 text-6xl mb-4">👥</div>
                          <p className="text-gray-500 text-sm">Nenhum atendimento por outros profissionais</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ÁREA DE CONTEÚDO PRINCIPAL */}
          <div className="xl:col-span-3">
            <div className="bg-white rounded-2xl shadow-lg border border-blue-50 p-6">
              <div className="text-center py-12">
                <div className="text-6xl mb-4">👨‍⚕️</div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Sistema de Prontuário Eletrônico</h2>
                <p className="text-gray-300 max-w-2xl mx-auto">
                  Selecione uma das opções ao lado para visualizar os atendimentos, receituários, 
                  laudos e outros documentos do paciente. Todos os dados estão organizados de forma 
                  segura e acessível para melhor acompanhamento do histórico médico.
                </p>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
                  <div className="bg-blue-50 p-4 rounded-xl text-center">
                    <div className="text-2xl mb-2">📊</div>
                    <h3 className="font-semibold text-blue-800">Histórico Completo</h3>
                    <p className="text-sm text-blue-600 mt-1">Acesse todo o histórico de atendimentos</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-xl text-center">
                    <div className="text-2xl mb-2">💊</div>
                    <h3 className="font-semibold text-green-800">Documentos</h3>
                    <p className="text-sm text-green-600 mt-1">Receituários, laudos e atestados</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-xl text-center">
                    <div className="text-2xl mb-2">🔒</div>
                    <h3 className="font-semibold text-purple-800">Seguro</h3>
                    <p className="text-sm text-purple-600 mt-1">Dados protegidos e confidenciais</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Atendimento;